import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** 1 ページの件数。 */
export const PROJECTS_PAGE_SIZE = 50;

/** 出荷状況の絞り込み。`all` は出荷済み・未出荷の両方。 */
export type ShippingFilter = "all" | "unshipped" | "shipped";

export interface ProjectListParamsState {
  page: number;
  keyword: string;
  shipping: ShippingFilter;
  /** true で「見積未紐付けの明細を持つ案件」だけに絞る（特急の後付け漏れ）。 */
  unlinkedOnly: boolean;
  sortKey: string;
  ascending: boolean;
}

const DEFAULTS: ProjectListParamsState = {
  page: 1,
  keyword: "",
  shipping: "all",
  unlinkedOnly: false,
  // 工番は採番順＝作成順なので、created_at で並べるのと結果は同じ。列として
  // 存在するキーにしておくことで、どの列で並んでいるかが一覧の矢印で分かる。
  sortKey: "project_number",
  ascending: false,
};

/**
 * 案件一覧の検索条件・並び替え・ページを URL のクエリに持つ。
 *
 * 見積一覧（[[useQuotationListParams]]）と同じ方針。
 */
export function useProjectListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ProjectListParamsState>(() => {
    const page = Number(searchParams.get("page") ?? DEFAULTS.page);
    const shipping = searchParams.get("shipping");
    return {
      page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
      keyword: searchParams.get("keyword") ?? DEFAULTS.keyword,
      shipping:
        shipping === "unshipped" || shipping === "shipped"
          ? shipping
          : DEFAULTS.shipping,
      unlinkedOnly: searchParams.get("unlinked") === "true",
      sortKey: searchParams.get("sort_key") ?? DEFAULTS.sortKey,
      ascending: searchParams.get("ascending") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<ProjectListParamsState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };
          // 条件を変えたらページを 1 に戻す（page 自体の変更を除く）。
          if (patch.page === undefined) merged.page = 1;

          const set = (key: string, value: string, fallback: string) =>
            value === fallback ? next.delete(key) : next.set(key, value);

          set("page", String(merged.page), String(DEFAULTS.page));
          set("keyword", merged.keyword, DEFAULTS.keyword);
          set("shipping", merged.shipping, DEFAULTS.shipping);
          set("unlinked", String(merged.unlinkedOnly), String(DEFAULTS.unlinkedOnly));
          set("sort_key", merged.sortKey, DEFAULTS.sortKey);
          set("ascending", String(merged.ascending), String(DEFAULTS.ascending));
          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** API に渡す shipped。両方のときは未指定にする。 */
  const shippedParam =
    params.shipping === "all" ? undefined : params.shipping === "shipped";

  /** API に渡す unlinked。絞らないときは未指定にする。 */
  const unlinkedParam = params.unlinkedOnly ? true : undefined;

  return { params, update, reset, shippedParam, unlinkedParam };
}
